import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { CompanyResearchActionCards } from "../components/CompanyResearchActionCards";
import { CompanyResearchLoadingNotice } from "../components/CompanyResearchLoadingNotice";
import { CompanyResearchMarkdown } from "../components/CompanyResearchMarkdown";
import {
  CompanyResearchSelfUrlModal,
  shouldPromptForSelfCompanyUrl,
} from "../components/CompanyResearchSelfUrlModal";
import type {
  CompanyResearchAvailableAction,
  CompanyResearchCandidate,
  CompanyResearchCandidateSearchResponse,
  CompanyResearchIntentType,
  CompanyResearchQuickSummaryResponse,
} from "../lib/companyResearch";
import { companyResearchClient } from "../lib/companyResearchClient";
import { AppError } from "../lib/errors";
import {
  clearCompanyResearchSession,
  loadCompanyResearchSession,
  saveCompanyResearchSession,
  type CompanyResearchSessionForm,
} from "../lib/companyResearchSession";

type LoadingState = "idle" | "searching" | "summary" | "job";
type CompanyResearchLocationState = { startNew?: boolean } | null;

const INITIAL_FORM: CompanyResearchSessionForm = {
  companyName: "",
  companyUrl: "",
  location: "",
  representativeName: "",
  industry: "",
};

const DEFAULT_ACTIONS: CompanyResearchAvailableAction[] = [
  { action_type: "learn_more", label: "詳しく調べる" },
  { action_type: "sales", label: "売り込みたい" },
  { action_type: "partnership", label: "協業したい" },
  { action_type: "risk_check", label: "リスクを確認したい" },
];

function shouldStartNewResearch(state: unknown) {
  return Boolean((state as CompanyResearchLocationState)?.startNew);
}

function createErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return "企業リサーチの実行に失敗しました。時間をおいて再試行してください。";
}

function hasValue(value: string) {
  return value.trim().length > 0;
}

function buildSearchCompanyName(form: CompanyResearchSessionForm) {
  return [form.companyName, form.location, form.representativeName, form.industry]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(" ");
}

function hasAdditionalHints(form: CompanyResearchSessionForm) {
  return Boolean(form.companyUrl.trim() || form.location.trim() || form.representativeName.trim() || form.industry.trim());
}

function formatConfidence(confidence?: string) {
  if (confidence === "high") return "高";
  if (confidence === "medium") return "中";
  if (confidence === "low") return "低";
  return undefined;
}

function normalizeCandidateText(value?: string | null) {
  return value?.replace(/\s+/g, " ").trim() ?? "";
}

function trimSearchSnippetTail(value: string) {
  const snippetIndex = value.search(/[（(](?:商号|公式サイト|公開|実績|詳細|詳|検索結果)/);
  const trimmed = snippetIndex > 0 ? value.slice(0, snippetIndex) : value;
  return trimmed.replace(/[.。．\s]+$/g, "").trim();
}

function getCandidateTitle(candidate: CompanyResearchCandidate) {
  const companyName = trimSearchSnippetTail(normalizeCandidateText(candidate.company_name));
  if (companyName) return companyName;

  const displayName = trimSearchSnippetTail(normalizeCandidateText(candidate.display_name));
  return displayName || "企業名未設定";
}

function getCandidateDisplayName(candidate: CompanyResearchCandidate, title: string) {
  const displayName = trimSearchSnippetTail(normalizeCandidateText(candidate.display_name));
  if (!displayName || displayName === title || displayName === normalizeCandidateText(candidate.company_name)) {
    return "";
  }
  return displayName;
}

function getCompanyNameFromQuickSummary(summary: CompanyResearchQuickSummaryResponse, fallback: string) {
  return (
    summary.structured_result?.company?.company_name ||
    summary.structured_result?.company?.name ||
    summary.candidate?.company_name ||
    fallback
  );
}

function getCompanyUrlFromQuickSummary(summary: CompanyResearchQuickSummaryResponse, fallback?: string | null) {
  return (
    summary.structured_result?.company?.official_url ||
    summary.structured_result?.company?.official_website ||
    summary.candidate?.company_url ||
    fallback ||
    null
  );
}

function CandidateCard({
  candidate,
  loading,
  onSelect,
}: {
  candidate: CompanyResearchCandidate;
  loading: boolean;
  onSelect: (candidate: CompanyResearchCandidate) => void;
}) {
  const url = candidate.company_url || candidate.source_url;
  const confidence = formatConfidence(candidate.confidence);
  const title = getCandidateTitle(candidate);
  const displayName = getCandidateDisplayName(candidate, title);

  return (
    <article className="overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="break-words text-base font-black leading-7 text-[#5D4D46] [overflow-wrap:anywhere]">
            {title}
          </h2>
          {displayName && (
            <p className="mt-1 break-words text-xs font-bold leading-5 text-[#8A746A] [overflow-wrap:anywhere]">
              検索結果名: {displayName}
            </p>
          )}
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block break-all text-sm font-bold text-[#A67C52] underline underline-offset-2 [overflow-wrap:anywhere]"
            >
              {url}
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={() => onSelect(candidate)}
          disabled={loading}
          className="min-h-[42px] w-full shrink-0 rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:bg-[#CBB9AF] sm:w-auto"
        >
          {loading ? "概要を取得中..." : "この企業で概要を見る"}
        </button>
      </div>

      {(candidate.location || candidate.business_summary || candidate.candidate_reason || confidence) && (
        <dl className="mt-3 grid gap-3 text-sm leading-6 text-[#6B5851] sm:grid-cols-2">
          {candidate.location && (
            <div className="min-w-0">
              <dt className="font-black text-[#5D4D46]">所在地</dt>
              <dd className="break-words [overflow-wrap:anywhere]">{candidate.location}</dd>
            </div>
          )}
          {candidate.business_summary && (
            <div className="min-w-0">
              <dt className="font-black text-[#5D4D46]">検索結果の抜粋</dt>
              <dd className="break-words [overflow-wrap:anywhere]">{candidate.business_summary}</dd>
            </div>
          )}
          {candidate.candidate_reason && (
            <div className="min-w-0">
              <dt className="font-black text-[#5D4D46]">選定理由</dt>
              <dd className="break-words [overflow-wrap:anywhere]">{candidate.candidate_reason}</dd>
            </div>
          )}
          {confidence && (
            <div className="min-w-0">
              <dt className="font-black text-[#5D4D46]">確度</dt>
              <dd>{confidence}</dd>
            </div>
          )}
        </dl>
      )}
    </article>
  );
}

function HintFields({
  form,
  onChange,
}: {
  form: CompanyResearchSessionForm;
  onChange: (next: CompanyResearchSessionForm) => void;
}) {
  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      <label className="block">
        <span className="text-xs font-black text-[#5D4D46]">公式サイトURL</span>
        <input
          type="url"
          value={form.companyUrl}
          onChange={(event) => onChange({ ...form, companyUrl: event.target.value })}
          className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6]"
          placeholder="https://example.co.jp/"
        />
      </label>
      <label className="block">
        <span className="text-xs font-black text-[#5D4D46]">所在地</span>
        <input
          type="text"
          value={form.location}
          onChange={(event) => onChange({ ...form, location: event.target.value })}
          className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6]"
          placeholder="例: 東京都"
        />
      </label>
      <label className="block">
        <span className="text-xs font-black text-[#5D4D46]">代表者名</span>
        <input
          type="text"
          value={form.representativeName}
          onChange={(event) => onChange({ ...form, representativeName: event.target.value })}
          className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6]"
          placeholder="例: 山田 太郎"
        />
      </label>
      <label className="block">
        <span className="text-xs font-black text-[#5D4D46]">業種・事業内容</span>
        <input
          type="text"
          value={form.industry}
          onChange={(event) => onChange({ ...form, industry: event.target.value })}
          className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6]"
          placeholder="例: ITサービス"
        />
      </label>
    </div>
  );
}

function AdditionalHintPanel({
  form,
  loading,
  onChangeForm,
  onSubmit,
}: {
  form: CompanyResearchSessionForm;
  loading: boolean;
  onChangeForm: (next: CompanyResearchSessionForm) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="mt-5 rounded-2xl border border-[#EADBC8] bg-white p-4">
      <p className="text-sm font-black text-[#5D4D46]">探している会社が見つかりませんか？</p>
      <p className="mt-2 text-sm leading-6 text-[#6B5851]">
        公式URL、所在地、代表者名、業種などを追加すると絞り込めます。
      </p>
      <p className="mt-2 text-xs font-bold leading-5 text-[#8A746A]">
        公式URLが分かる場合は、URLを入力するとすぐ概要を確認できます。所在地や業種を入力すると、候補を絞り込めます。
      </p>
      <HintFields form={form} onChange={onChangeForm} />
      <button
        type="button"
        onClick={onSubmit}
        disabled={loading || !hasAdditionalHints(form)}
        className="mt-4 min-h-[42px] rounded-full bg-[#7C5A4E] px-5 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:bg-[#CBB9AF]"
      >
        追加情報で候補を探す
      </button>
    </div>
  );
}

function CandidateSearchPanel({
  searchResult,
  form,
  loading,
  onSelectCandidate,
  onChangeForm,
  onSubmitHints,
}: {
  searchResult: CompanyResearchCandidateSearchResponse;
  form: CompanyResearchSessionForm;
  loading: boolean;
  onSelectCandidate: (candidate: CompanyResearchCandidate) => void;
  onChangeForm: (next: CompanyResearchSessionForm) => void;
  onSubmitHints: () => void;
}) {
  const candidates = searchResult.candidates ?? [];

  if (
    searchResult.recommended_action === "retry_later" ||
    searchResult.status === "failed" ||
    searchResult.status === "search_failed"
  ) {
    return (
      <section className="rounded-2xl border border-[#F0C0B0] bg-[#FFF8F6] p-5">
        <p className="text-sm font-black text-[#B4533A]">検索が一時的に失敗しました</p>
        <p className="mt-2 text-sm leading-6 text-[#6B5851]">
          {searchResult.message || "時間をおいて再実行してください。"}
        </p>
      </section>
    );
  }

  const needsMoreInfo =
    searchResult.recommended_action === "ask_user_hint" ||
    searchResult.resolution_mode === "needs_hint" ||
    searchResult.resolution_mode === "needs_more_info";
  const isSingle = searchResult.resolution_mode === "single_high_confidence" && candidates.length === 1;
  const shouldShowHintPanel =
    candidates.length >= 4 ||
    searchResult.resolution_mode === "user_selection" ||
    searchResult.recommended_action === "select_candidate" ||
    needsMoreInfo;

  return (
    <section className="rounded-2xl border border-[#F0D0B8] bg-[#FFF8F2] p-5">
      <p className="text-base font-black leading-7 text-[#7C5A4E]">
        {isSingle
          ? "この会社でよさそうです。概要を見る前に確認してください。"
          : needsMoreInfo
            ? "候補は見つかりましたが、公式URLを確認できませんでした。追加情報を入力してください。"
            : "同名または類似名の企業候補が複数見つかりました。調査対象を選んでください。"}
      </p>

      {searchResult.message && <p className="mt-2 text-sm leading-6 text-[#6B5851]">{searchResult.message}</p>}

      {candidates.length > 0 && (
        <div className="mt-4 grid gap-3">
          {candidates.map((candidate) => (
            <CandidateCard
              key={candidate.candidate_id}
              candidate={candidate}
              loading={loading}
              onSelect={onSelectCandidate}
            />
          ))}
        </div>
      )}

      {shouldShowHintPanel && (
        <AdditionalHintPanel form={form} loading={loading} onChangeForm={onChangeForm} onSubmit={onSubmitHints} />
      )}
    </section>
  );
}

function QuickSummaryPanel({
  summary,
  form,
  loading,
  onChangeForm,
  onSubmitHints,
  onCreateJob,
}: {
  summary: CompanyResearchQuickSummaryResponse;
  form: CompanyResearchSessionForm;
  loading: boolean;
  onChangeForm: (next: CompanyResearchSessionForm) => void;
  onSubmitHints: () => void;
  onCreateJob: (intentType: CompanyResearchIntentType) => void;
}) {
  if (summary.status === "failed") {
    return (
      <section className="rounded-2xl border border-[#F0C0B0] bg-[#FFF8F6] p-5">
        <p className="text-sm font-black text-[#B4533A]">簡易概要の作成に失敗しました</p>
        <p className="mt-2 text-sm leading-6 text-[#6B5851]">
          {summary.message || "時間をおいて再実行してください。"}
        </p>
      </section>
    );
  }

  if (summary.status === "needs_more_info") {
    return (
      <section className="rounded-2xl border border-[#F0D0B8] bg-[#FFF8F2] p-5">
        <p className="text-base font-black leading-7 text-[#7C5A4E]">
          {summary.message || "候補企業は見つかりましたが、公式URLを確認できませんでした。"}
        </p>
        {summary.requested_hints && summary.requested_hints.length > 0 && (
          <p className="mt-2 text-sm leading-6 text-[#6B5851]">
            追加情報: {summary.requested_hints.join("、")}
          </p>
        )}
        <AdditionalHintPanel form={form} loading={loading} onChangeForm={onChangeForm} onSubmit={onSubmitHints} />
      </section>
    );
  }

  const company = summary.structured_result?.company;
  const sources = summary.structured_result?.quick_sources ?? [];
  const actions = summary.structured_result?.available_actions?.length
    ? summary.structured_result.available_actions
    : DEFAULT_ACTIONS;

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-[#EADBC8] bg-white p-5">
        {summary.display_text ? (
          <CompanyResearchMarkdown text={summary.display_text} />
        ) : (
          <div className="grid gap-2 text-sm leading-6 text-[#5D4D46]">
            <p className="font-black">{company?.company_name || company?.name || "簡易企業概要"}</p>
            {company?.official_url && (
              <p className="break-all [overflow-wrap:anywhere]">公式サイトURL: {company.official_url}</p>
            )}
            {company?.official_website && (
              <p className="break-all [overflow-wrap:anywhere]">公式サイトURL: {company.official_website}</p>
            )}
            {company?.business_summary && <p>事業概要: {company.business_summary}</p>}
          </div>
        )}
      </section>

      {sources.length > 0 && (
        <section className="rounded-2xl border border-[#EADBC8] bg-white p-5">
          <h2 className="text-sm font-black text-[#5D4D46]">参照元</h2>
          <ul className="mt-3 space-y-2 text-sm leading-6">
            {sources.map((source, index) => (
              <li key={`${source.url}-${index}`}>
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all font-bold text-[#A67C52] underline underline-offset-2 [overflow-wrap:anywhere]"
                  >
                    {source.title || source.url}
                  </a>
                ) : (
                  source.title
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-2xl border border-[#EADBC8] bg-[#FFFCFA] p-5">
        <h2 className="text-sm font-black text-[#5D4D46]">追加分析</h2>
        <p className="mt-2 text-xs font-bold leading-5 text-[#8A746A]">
          この確認事項をもとに、次の分析へ進めます。
        </p>
        <CompanyResearchActionCards actions={actions} disabled={loading} onCreateJob={onCreateJob} />
      </section>
    </div>
  );
}

export default function RequestCompanyResearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [restoredSession] = useState(() => {
    if (shouldStartNewResearch(location.state)) {
      clearCompanyResearchSession();
      return null;
    }
    return loadCompanyResearchSession();
  });
  const [form, setForm] = useState<CompanyResearchSessionForm>(() => restoredSession?.form ?? INITIAL_FORM);
  const [candidateSearch, setCandidateSearch] = useState<CompanyResearchCandidateSearchResponse | null>(
    () => restoredSession?.candidateSearch ?? null
  );
  const [selectedCandidate, setSelectedCandidate] = useState<CompanyResearchCandidate | null>(
    () => restoredSession?.quickSummary?.candidate ?? null
  );
  const [quickSummary, setQuickSummary] = useState<CompanyResearchQuickSummaryResponse | null>(
    () => restoredSession?.quickSummary ?? null
  );
  const [loadingState, setLoadingState] = useState<LoadingState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingSelfCompanyIntent, setPendingSelfCompanyIntent] = useState<CompanyResearchIntentType | null>(null);
  const quickSummaryRef = useRef<HTMLDivElement>(null);
  const prevQuickSummaryRef = useRef<typeof quickSummary>(null);

  const loading = loadingState !== "idle";

  useEffect(() => {
    if (quickSummary && quickSummary !== prevQuickSummaryRef.current) {
      const el = quickSummaryRef.current;
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }
    }
    prevQuickSummaryRef.current = quickSummary;
  }, [quickSummary]);
  const canSearch = hasValue(form.companyName) && !loading;

  const persistSession = (
    nextForm: CompanyResearchSessionForm,
    nextCandidateSearch = candidateSearch,
    nextQuickSummary = quickSummary
  ) => {
    saveCompanyResearchSession({
      form: nextForm,
      candidateSearch: nextCandidateSearch ?? undefined,
      quickSummary: nextQuickSummary ?? undefined,
    });
  };

  const updateForm = (nextForm: CompanyResearchSessionForm) => {
    setForm(nextForm);
    persistSession(nextForm);
  };

  const searchCandidates = async (searchCompanyName?: string) => {
    const companyName = (searchCompanyName ?? form.companyName).trim();
    if (!companyName || loading) return;

    setLoadingState("searching");
    setErrorMessage(null);
    setCandidateSearch(null);
    setQuickSummary(null);
    setSelectedCandidate(null);
    persistSession(form, null, null);

    try {
      const nextSearch = await companyResearchClient.searchCandidates({
        company_name: companyName,
      });

      setCandidateSearch(nextSearch);
      persistSession(form, nextSearch, null);
    } catch (error) {
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      setCandidateSearch(null);
      setErrorMessage(createErrorMessage(error));
    } finally {
      setLoadingState("idle");
    }
  };

  const createQuickSummary = async (
    candidate?: CompanyResearchCandidate | null,
    directUrl?: { companyUrl: string; sourceUrl: string }
  ) => {
    const resolvedCandidate = candidate === undefined && !directUrl ? selectedCandidate : candidate;
    const companyName = (directUrl ? form.companyName : resolvedCandidate?.company_name || form.companyName).trim();
    if (!companyName || loading) return;
    const currentSearch = candidateSearch;

    setLoadingState("summary");
    setErrorMessage(null);
    setCandidateSearch(null);
    setQuickSummary(null);
    if (resolvedCandidate) {
      setSelectedCandidate(resolvedCandidate);
    }

    try {
      const nextSummary = await companyResearchClient.createQuickSummary({
        search_id: directUrl ? undefined : currentSearch?.search_id,
        candidate_id: directUrl ? undefined : resolvedCandidate?.candidate_id,
        company_name: companyName,
        company_url: directUrl?.companyUrl || form.companyUrl.trim() || resolvedCandidate?.company_url || null,
        source_url: directUrl?.sourceUrl || resolvedCandidate?.source_url || null,
        business_summary: resolvedCandidate?.business_summary || undefined,
      });

      setQuickSummary(nextSummary);
      persistSession(form, null, nextSummary);
    } catch (error) {
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      setErrorMessage(createErrorMessage(error));
    } finally {
      setLoadingState("idle");
    }
  };

  const submitAdditionalHints = async () => {
    if (!form.companyName.trim() || loading || !hasAdditionalHints(form)) return;

    const companyUrl = form.companyUrl.trim();
    if (companyUrl) {
      setSelectedCandidate(null);
      await createQuickSummary(null, { companyUrl, sourceUrl: companyUrl });
      return;
    }

    await searchCandidates(buildSearchCompanyName(form));
  };

  const createDetailedJob = async (intentType: CompanyResearchIntentType, selfCompanyUrl?: string | null) => {
    if (!quickSummary || quickSummary.status !== "completed" || loading) return;

    if (!quickSummary.quick_summary_id) {
      setErrorMessage("追加分析に必要な概要IDを確認できませんでした。もう一度簡易概要を作成してください。");
      return;
    }

    setLoadingState("job");
    setErrorMessage(null);

    try {
      await companyResearchClient.createJob({
        quick_summary_id: quickSummary.quick_summary_id,
        quick_summary: quickSummary,
        base_job_id: quickSummary.quick_summary_id,
        intent_type: intentType,
        company_name: getCompanyNameFromQuickSummary(quickSummary, selectedCandidate?.company_name || form.companyName),
        company_url: getCompanyUrlFromQuickSummary(quickSummary, selectedCandidate?.company_url || form.companyUrl),
        self_company_url: selfCompanyUrl?.trim() || null,
        email_delivery: false,
      });

      navigate(`/request/company-research/jobs/${encodeURIComponent(quickSummary.quick_summary_id)}`);
    } catch (error) {
      if (error instanceof AppError && error.code === "SESSION_EXPIRED") {
        navigate("/splash", { replace: true });
        return;
      }
      setErrorMessage(createErrorMessage(error));
      setLoadingState("idle");
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void searchCandidates();
  };

  const requestDetailedJob = (intentType: CompanyResearchIntentType) => {
    if (shouldPromptForSelfCompanyUrl(intentType)) {
      setPendingSelfCompanyIntent(intentType);
      return;
    }
    void createDetailedJob(intentType, null);
  };

  const submitSelfCompanyUrl = (selfCompanyUrl: string | null) => {
    const intentType = pendingSelfCompanyIntent;
    setPendingSelfCompanyIntent(null);
    if (!intentType) return;
    void createDetailedJob(intentType, selfCompanyUrl);
  };

  const clearResearch = () => {
    setForm(INITIAL_FORM);
    setCandidateSearch(null);
    setSelectedCandidate(null);
    setQuickSummary(null);
    setErrorMessage(null);
    setLoadingState("idle");
    clearCompanyResearchSession();
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/request" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">企業リサーチ</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">企業リサーチ</p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-[#7C5A4E]">企業リサーチ</h1>
            </div>
            <Link
              to="/request/company-research/jobs"
              className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2]"
            >
              調査履歴を見る
            </Link>
          </div>

          <form className="mt-6 grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]" onSubmit={handleSubmit}>
            <label className="block">
              <span className="text-sm font-black text-[#5D4D46]">企業名</span>
              <input
                type="text"
                value={form.companyName}
                onChange={(event) => updateForm({ ...form, companyName: event.target.value })}
                className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-base text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#F1D8C6]"
                placeholder="例: 株式会社ウィズダムネットワークス"
              />
            </label>

            <div className="flex items-end">
              <button
                type="submit"
                disabled={!canSearch}
                className="min-h-[50px] w-full rounded-full bg-[#7C5A4E] px-6 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(124,90,78,0.20)] transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:bg-[#CBB9AF] disabled:shadow-none md:w-auto"
              >
                {loadingState === "searching" ? "検索中..." : "企業候補を探す"}
              </button>
            </div>
          </form>

          <p className="mt-4 text-xs font-bold leading-5 text-[#8A746A]">
            まず候補を確認し、簡易概要を見てから追加分析へ進みます。
          </p>

          {(candidateSearch || quickSummary || errorMessage) && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearResearch}
                className="rounded-full border border-[#D8C0AF] bg-white px-5 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2]"
              >
                入力をリセットして別の企業を調べる
              </button>
            </div>
          )}

          {errorMessage && <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>}
        </section>

        <section
          aria-label="企業リサーチ結果"
          className="mt-5 min-h-[360px] rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          <div className="space-y-4">
            {loadingState === "searching" && (
              <CompanyResearchLoadingNotice title="企業候補を探しています..." />
            )}

            {loadingState === "summary" && (
              <CompanyResearchLoadingNotice title="選択した企業の概要を作成しています..." />
            )}

            {loadingState === "job" && (
              <CompanyResearchLoadingNotice title="詳細調査を開始しています..." />
            )}

            {candidateSearch && (
              <CandidateSearchPanel
                searchResult={candidateSearch}
                form={form}
                loading={loading}
                onSelectCandidate={(candidate) => void createQuickSummary(candidate)}
                onChangeForm={updateForm}
                onSubmitHints={() => void submitAdditionalHints()}
              />
            )}

            {quickSummary && (
              <div ref={quickSummaryRef}>
                <QuickSummaryPanel
                  summary={quickSummary}
                  form={form}
                  loading={loading}
                  onChangeForm={updateForm}
                  onSubmitHints={() => void submitAdditionalHints()}
                  onCreateJob={requestDetailedJob}
                />
              </div>
            )}

            {!loading && !candidateSearch && !quickSummary && (
              <div className="flex min-h-[320px] items-center justify-center rounded-[1.5rem] border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center text-sm leading-7 text-[#6B5851]">
                企業名を入力すると、候補と簡易概要がここに表示されます。
              </div>
            )}
          </div>
        </section>
      </div>
      <CompanyResearchSelfUrlModal
        intentType={pendingSelfCompanyIntent}
        onClose={() => setPendingSelfCompanyIntent(null)}
        onSubmit={submitSelfCompanyUrl}
      />
    </main>
  );
}
